import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'

type FieldRenderProps = {
  fieldId: string
  describedBy?: string
  invalid?: boolean
}

type FormFieldProps = {
  id: string
  label: ReactNode
  required?: boolean
  description?: ReactNode
  error?: ReactNode
  className?: string
  children: (props: FieldRenderProps) => ReactNode
}

export function FormField({
  id,
  label,
  required = false,
  description,
  error,
  className,
  children,
}: FormFieldProps) {
  const descriptionId = description ? `${id}-description` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-foreground">
        {label}
        {required && <span className="ml-1 text-destructive before:content-['*']" aria-hidden="true" />}
      </label>
      {description && (
        <p id={descriptionId} className="text-xs leading-5 text-muted-foreground">
          {description}
        </p>
      )}
      {children({ fieldId: id, describedBy, invalid: !!error })}
      {error && (
        <p id={errorId} className="text-xs font-medium text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

type FormActionsProps = {
  cancelLabel?: string
  submitLabel: string
  onCancel?: () => void
  isSubmitting?: boolean
  submitDisabled?: boolean
  className?: string
}

export function FormActions({
  cancelLabel,
  submitLabel,
  onCancel,
  isSubmitting = false,
  submitDisabled = false,
  className,
}: FormActionsProps) {
  return (
    <div className={cn('mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end', className)}>
      {cancelLabel && onCancel && (
        <button
          type="button"
          className="inline-flex min-h-10 items-center justify-center rounded-md border border-input px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {cancelLabel}
        </button>
      )}
      <button
        type="submit"
        className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={isSubmitting || submitDisabled}
      >
        {submitLabel}
      </button>
    </div>
  )
}
